import { type QueryObserverResult, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useFlags } from '@repo/poly-hooks/flags';
import {
  type FourthlineProviderDetails,
  type GetIdentityVerificationResponse,
  type IdnowProviderDetails,
  IdnowProviderDetailsErrorReason,
  KycProvider,
  kycQueries,
  type RequestCredentialsResponse,
} from '@repo/api-kit/queries/due-diligence/kyc';
import {
  type KycProviderDetailsContextValue,
  type RefetchKyc,
  useKycProviderDetails,
} from '#context/provider-details-context';
import { mapApStatusToLegacy } from '#utils/map-ap-status-to-legacy';
import { useRefetchProviderDetails } from './use-refetch-provider-details';

const idnowErrorReasons = Object.values(IdnowProviderDetailsErrorReason);

type SupportedProvider = typeof KycProvider.fourthline | typeof KycProvider.idnow;

interface BaseUseProviderDetailsOptions {
  identityVerificationId?: string | null;
  refetchAcceptance: RefetchKyc;
}

interface FourthlineProviderDetailsOptions extends BaseUseProviderDetailsOptions {
  provider: typeof KycProvider.fourthline;
}

interface IdnowProviderDetailsOptions extends BaseUseProviderDetailsOptions {
  provider: typeof KycProvider.idnow;
}

type UseProviderDetailsOptions = FourthlineProviderDetailsOptions | IdnowProviderDetailsOptions;
type NormalizedIdnowProviderDetails = Pick<IdnowProviderDetails, 'reason' | 'status' | 'token'> & {
  outcome?: GetIdentityVerificationResponse['outcome'];
};
type ProviderDetails = FourthlineProviderDetails | NormalizedIdnowProviderDetails;
type RefetchCredentials = () => Promise<QueryObserverResult<RequestCredentialsResponse, Error>>;
type ProviderDetailsQueryResult<T> = Omit<
  UseQueryResult<RequestCredentialsResponse, Error>,
  'data' | 'refetch'
> & {
  data: T | null;
  refetch: RefetchCredentials | RefetchKyc;
};

function toIdnowErrorReason(providerDetails: unknown) {
  return (
    idnowErrorReasons.find(reason => reason === providerDetails) ??
    IdnowProviderDetailsErrorReason.ERROR_REASON_UNSPECIFIED
  );
}

function getContextProviderDetails(
  provider: SupportedProvider,
  context: KycProviderDetailsContextValue
) {
  switch (provider) {
    case KycProvider.fourthline:
      return context.providerDetails?.$case === 'fourthlineProviderDetails'
        ? context.providerDetails.value
        : null;
    case KycProvider.idnow:
      return context.providerDetails?.$case === 'idnowProviderDetails'
        ? context.providerDetails.value
        : null;
    default: {
      const exhaustiveCheck: never = provider;
      return exhaustiveCheck;
    }
  }
}

function getIdvProviderDetails(
  provider: SupportedProvider,
  credentials?: RequestCredentialsResponse,
  identityVerification?: GetIdentityVerificationResponse
) {
  if (!credentials) return null;

  switch (provider) {
    case KycProvider.fourthline:
      return {
        token: credentials.providerToken,
        redirectUrl: credentials.redirectUrl,
      };
    case KycProvider.idnow:
      return {
        token: credentials.providerToken,
        outcome: identityVerification?.outcome,
        status: mapApStatusToLegacy(
          identityVerification?.status ?? credentials.status,
          identityVerification?.outcome?.code
        ),
        reason: toIdnowErrorReason(identityVerification?.outcome?.providerDetails),
      };
    default: {
      const exhaustiveCheck: never = provider;
      return exhaustiveCheck;
    }
  }
}

export function useProviderDetails(
  options: FourthlineProviderDetailsOptions
): ProviderDetailsQueryResult<FourthlineProviderDetails>;
export function useProviderDetails(
  options: IdnowProviderDetailsOptions
): ProviderDetailsQueryResult<NormalizedIdnowProviderDetails>;
export function useProviderDetails({
  provider,
  identityVerificationId,
  refetchAcceptance,
}: UseProviderDetailsOptions): ProviderDetailsQueryResult<ProviderDetails> {
  const { featureBooleanKycVerificationApGateway } = useFlags();
  const contextProviderDetails = useKycProviderDetails();
  const refetchContextDetails = useRefetchProviderDetails(refetchAcceptance);
  const isIdvEnabled = Boolean(featureBooleanKycVerificationApGateway && identityVerificationId);

  const credentialsQuery = useQuery({
    ...kycQueries.credentials(identityVerificationId ?? ''),
    enabled: isIdvEnabled,
  });
  const statusQuery = useQuery({
    ...kycQueries.identityVerification(identityVerificationId ?? ''),
    enabled: isIdvEnabled && provider === KycProvider.idnow,
    refetchInterval: provider === KycProvider.idnow ? 1000 : false,
  });

  const data = isIdvEnabled
    ? getIdvProviderDetails(provider, credentialsQuery.data, statusQuery.data)
    : getContextProviderDetails(provider, contextProviderDetails);
  const isLoading = isIdvEnabled
    ? credentialsQuery.isLoading
    : contextProviderDetails.provider === provider && !data;

  return {
    ...credentialsQuery,
    data,
    error:
      credentialsQuery.error ??
      (!data && provider === KycProvider.idnow ? statusQuery.error : null),
    isLoading,
    refetch: isIdvEnabled ? credentialsQuery.refetch : refetchContextDetails,
  };
}
